import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  View,
  Text,
  Button,
  useActionStates,
  ErrorMessageEnum,
} from '@rivallapp/volosports-components';

import { RosterManagement } from '..';
import { ADD_PLAYER_TO_DROPIN } from '../../../apps/RFO/Manage/Main/LeagueStatusInfo/ManageDropIns/DROPIN_QUERIES_AND_MUTATIONS';
import styles from './styles';
import { SearchAndAddUsersModal } from '../SearchAndAddUsers';

type TeamType = {
  _id: string;
  name: string;
};

interface DropInRosterProps {
  leagueId: string;
  currentGame: {
    _id: string;
    teams: Array<TeamType>;
  };
  isPast?: boolean;
  scheduleRefetch: () => void | undefined;
}

const DropInRoster = ({
  leagueId,
  currentGame,
  isPast = false,
  scheduleRefetch,
}: DropInRosterProps) => {
  // updateGameRoster and setUpdateGameRoster are passed into GameRoster
  // to trigger a refetch (in GameRoster) when addDropIn is called
  const [updateGameRoster, setUpdateGameRoster] = useState(false);
  // manually adding player to dropin slot
  // selectedDropin is passed through GameRoster to DropInPill, sending back a slot ID
  const [selectedDropin, setSelectedDropin] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');

  const { setSuccess, setError, showAlert, setShowAlert, alertMessage, alertType } =
    useActionStates({
      withAlerts: true,
    });

  const teamOneId = currentGame?.teams?.[0]?._id;
  const teamTwoId = currentGame?.teams?.[1]?._id;
  const teamOneName = currentGame?.teams?.[0]?.name;
  const teamTwoName = currentGame?.teams?.[1]?.name;

  // set selectedTeamId to teamOne on initial load & when changing weeks
  useEffect(() => {
    if (currentGame) setSelectedTeamId(teamOneId ?? '');
  }, [currentGame, teamOneId]);

  const otherTeamName = selectedTeamId === teamOneId ? teamTwoName : teamOneName;

  const [addPlayerToDropin, { loading: updating }] = useMutation(ADD_PLAYER_TO_DROPIN);

  const addDropin = async (userId: string) => {
    try {
      await addPlayerToDropin({
        variables: {
          input: {
            playerId: userId,
            dropInSlotId: selectedDropin,
          },
        },
      });
      setSelectedDropin('');
      setSuccess('Successfully added player to dropin slot.');
      setUpdateGameRoster(true);
      scheduleRefetch();
    } catch (error) {
      setError(ErrorMessageEnum.PLAYER_ALREADY_IN_LEAGUE);
    }
  };

  const handleToggle = () => {
    setSelectedTeamId((selectedTeamId === teamOneId ? teamTwoId : teamOneId) ?? '');
    setUpdateGameRoster(true);
  };

  return (
    <View style={styles.dropinContainer}>
      <View style={styles.dropinRoster}>
        <Button style={styles.switchButton} onPress={handleToggle}>
          <Text style={styles.switchText}>View {otherTeamName}</Text>
        </Button>
        {selectedTeamId ? (
          <RosterManagement
            gameId={currentGame?._id}
            teamId={selectedTeamId}
            leagueId={leagueId}
            updateGameRoster={updateGameRoster}
            setUpdateGameRoster={setUpdateGameRoster}
            isPast={isPast}
            isAdmin
            setSelectedDropin={setSelectedDropin}
          />
        ) : (
          <Text style={styles.unassignedText}>Cannot manage drop-ins on an Unassigned team</Text>
        )}
      </View>
      <SearchAndAddUsersModal
        isOpen={selectedDropin !== ''}
        onClose={() => setSelectedDropin('')}
        addUser={addDropin}
        updating={updating}
        headerText="Add a player manually"
        descriptionText="Search below to add a Volo user manually to this drop-in slot. Any players added in this way will not be charged."
        userButtonPressText="Assign To Drop-In"
        showAlert={showAlert}
        alertType={alertType}
        alertMessage={alertMessage}
        setShowAlert={setShowAlert}
      />
    </View>
  );
};

export default DropInRoster;
